import { useQuery } from "@apollo/client/react";
import { Navbar, Page } from "framework7-react";
import { useMemo } from "react";

import { ClockPieChart } from "@/components/ClockPieChart";
import { DimensionLegendBar } from "@/components/DimensionLegendBar";
import { EntrySummary } from "@/components/EntrySummary";
import { NavLeftEmpty } from "@/components/NavLeftEmpty";
import { NavRightEmpty } from "@/components/NavRightEmpty";
import { NavTitle } from "@/components/NavTitle";
import { formatShortDateWithYear } from "@/config/date-fns";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { getFragmentData } from "@/generated/graphql";
import { EntryFragmentDoc, GetTodayDataDocument } from "@/generated/graphql/graphql";
import { usePageReady } from "@/hooks/usePageReady";
import { useToday } from "@/hooks/useToday";

export const TopPage = createPage("TopPage", PageComponent);

function PageComponent({ pageName }: PageComponentProps) {
  const { isPageReady, getPageProps } = usePageReady(pageName, { defaultReady: true, isTabPage: true });

  const today = useToday();
  const title = useMemo(() => formatShortDateWithYear(today), [today]);

  const { data, refetch } = useQuery(GetTodayDataDocument, { skip: !isPageReady });
  const currentEntry = getFragmentData(EntryFragmentDoc, data?.currentEntry);
  const entries = getFragmentData(EntryFragmentDoc, data?.todayEntries || []);

  return (
    <Page
      ptr
      ptrMousewheel
      data-component={pageName}
      onPtrRefresh={(done) => {
        refetch().then(done);
      }}
      {...getPageProps()}
    >
      <Navbar>
        <NavLeftEmpty />
        <NavTitle>{title}</NavTitle>
        <NavRightEmpty />
      </Navbar>
      <EntrySummary entry={currentEntry} />
      <ClockPieChart
        isDisplayHand
        baseDate={today}
        entries={entries}
      />
      <DimensionLegendBar />
    </Page>
  );
}
